import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/http.js';
import { useTheme } from '../../hooks/useTheme.jsx';

const RegisterPage = () => {
  const { theme, toggleTheme } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [acceptedConsent, setAcceptedConsent] = useState(false);
  const [gender, setGender] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!birthDate || !acceptedConsent) return;
    setError('');
    setLoading(true);
    try {
      const { res, data } = await apiClient.register({
        email,
        password,
        full_name: fullName,
        birth_date: birthDate,
        consent_accepted: acceptedConsent,
        gender: gender || null,
        terms_version: '2026-05-20',
        policy_version: '2026-05-20',
      });
      if (res.ok) {
        navigate(`/verify-email?email=${encodeURIComponent(email)}`);
      } else {
        setError(apiClient.parseApiError(data, 'Falha no cadastro.'));
      }
    } catch {
      setError('Erro de conexão.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden bg-background p-4">
      <div className="fixed top-[-20%] left-[-10%] w-[60vw] h-[60vw] rounded-full bg-primary-container/5 blur-[120px] pointer-events-none"></div>
      <div className="fixed bottom-[-20%] right-[-10%] w-[50vw] h-[50vw] rounded-full bg-primary-container/5 blur-[100px] pointer-events-none"></div>

      {/* Top bar */}
      <div className="fixed top-4 left-4 right-4 flex justify-between z-20">
        <button
          onClick={() => navigate('/')}
          title="Voltar ao início"
          className="flex items-center gap-2 text-on-surface-variant hover:text-primary transition-colors text-sm"
        >
          <span className="material-symbols-outlined text-[20px]">arrow_back</span>
          <span className="hidden sm:inline">Início</span>
        </button>
        <button
          onClick={toggleTheme}
          title={theme === 'dark' ? 'Modo claro' : 'Modo escuro'}
          className="p-2 rounded-full text-on-surface-variant hover:text-primary hover:bg-primary/10 transition-all"
        >
          <span className="material-symbols-outlined text-[20px]">{theme === 'dark' ? 'light_mode' : 'dark_mode'}</span>
        </button>
      </div>

      <main className="relative z-10 w-full max-w-md px-6 py-12 flex flex-col items-center">
        <div className="mb-8 flex flex-col items-center opacity-80">
          <img alt="CatholicGPT Logo" className="h-20 w-20 mx-auto rounded-full bg-white object-contain p-2 shadow-sm mb-6" src="/logo-192.png"/>
          <h1 className="font-headline text-3xl font-medium tracking-tight text-primary-container">CatholicGPT</h1>
        </div>

        <div className="w-full glass-panel rounded-xl border border-outline-variant/20 shadow-2xl p-8 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-32 h-32 bg-primary/10 blur-3xl rounded-full pointer-events-none"></div>

          <div className="text-center mb-6 relative z-10">
            <h2 className="font-headline text-2xl font-bold text-on-surface mb-2">Criar Conta</h2>
            <p className="font-body text-sm text-on-surface-variant">Comece sua jornada de fé.</p>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4 relative z-10">
            <div className="space-y-2">
              <label className="block font-label text-xs font-medium text-on-surface-variant uppercase tracking-wider">Nome Completo</label>
              <div className="relative">
                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant opacity-60 text-lg">person</span>
                <input
                  className="w-full bg-surface-container-lowest border-0 border-b border-outline-variant/30 rounded-t-lg pl-10 pr-4 py-3 text-on-surface placeholder:text-on-surface-variant/50 focus:ring-0 focus:border-primary transition-colors font-body"
                  placeholder="Seu nome" required value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block font-label text-xs font-medium text-on-surface-variant uppercase tracking-wider">E-mail</label>
              <div className="relative">
                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant opacity-60 text-lg">mail</span>
                <input
                  className="w-full bg-surface-container-lowest border-0 border-b border-outline-variant/30 rounded-t-lg pl-10 pr-4 py-3 text-on-surface placeholder:text-on-surface-variant/50 focus:ring-0 focus:border-primary transition-colors font-body"
                  placeholder="seu@email.com" required type="email" value={email}
                  autoComplete="email"
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block font-label text-xs font-medium text-on-surface-variant uppercase tracking-wider">Data de Nascimento</label>
              <div className="relative">
                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant opacity-60 text-lg">calendar_month</span>
                <input
                  className="w-full bg-surface-container-lowest border-0 border-b border-outline-variant/30 rounded-t-lg pl-10 pr-4 py-3 text-on-surface placeholder:text-on-surface-variant/50 focus:ring-0 focus:border-primary transition-colors font-body"
                  required type="date" value={birthDate}
                  autoComplete="bday"
                  onChange={(e) => setBirthDate(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block font-label text-xs font-medium text-on-surface-variant uppercase tracking-wider">Tratamento Gramatical (Opcional)</label>
              <div className="relative">
                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant opacity-60 text-lg">supervised_user_circle</span>
                <select
                  className="w-full bg-surface-container-lowest border-0 border-b border-outline-variant/30 rounded-t-lg pl-10 pr-10 py-3 text-on-surface focus:ring-0 focus:border-primary transition-colors font-body appearance-none"
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                >
                  <option value="" className="bg-surface text-on-surface">Prefiro não informar (Tratamento Neutro)</option>
                  <option value="M" className="bg-surface text-on-surface">Masculino (Tratamento: ele/dele)</option>
                  <option value="F" className="bg-surface text-on-surface">Feminino (Tratamento: ela/dela)</option>
                </select>
                <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-on-surface-variant opacity-60 pointer-events-none text-lg">arrow_drop_down</span>
              </div>
            </div>

            <div className="space-y-2">
              <label className="block font-label text-xs font-medium text-on-surface-variant uppercase tracking-wider">Senha</label>
              <div className="relative">
                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant opacity-60 text-lg">lock</span>
                <input
                  className="w-full bg-surface-container-lowest border-0 border-b border-outline-variant/30 rounded-t-lg pl-10 pr-10 py-3 text-on-surface placeholder:text-on-surface-variant/50 focus:ring-0 focus:border-primary transition-colors font-body"
                  placeholder="••••••••" required type={showPassword ? 'text' : 'password'} value={password}
                  autoComplete="new-password"
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button className="absolute inset-y-0 right-0 flex items-center pr-3 text-on-surface-variant opacity-60 hover:text-primary transition-colors" type="button" onClick={() => setShowPassword(!showPassword)}>
                  <span className="material-symbols-outlined text-lg">{showPassword ? 'visibility' : 'visibility_off'}</span>
                </button>
              </div>
            </div>

            {/* Checkbox de Consentimento LGPD */}
            <label className="flex items-start gap-3 p-3.5 rounded-xl bg-surface-container-lowest border border-outline-variant/20 cursor-pointer select-none hover:bg-surface-container-low transition-colors mt-2">
              <input
                type="checkbox"
                required
                checked={acceptedConsent}
                onChange={(e) => setAcceptedConsent(e.target.checked)}
                className="mt-0.5 h-5 w-5 rounded border-outline-variant text-primary focus:ring-primary accent-primary cursor-pointer"
              />
              <span className="text-xs font-medium leading-relaxed text-on-surface-variant">
                Li e aceito expressamente a{' '}
                <a href="/privacidade" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Política de Privacidade</a>
                {' '}e os{' '}
                <a href="/termos" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Termos de Uso</a>.
              </span>
            </label>

            {error && <p className="text-error text-sm text-center">{error}</p>}

            <button
              className="w-full bg-primary-container hover:bg-primary text-on-primary font-medium py-3 rounded-full mt-2 transition-all hover:shadow-[0_0_16px_rgba(212,175,55,0.3)] flex items-center justify-center gap-2 group disabled:opacity-40 disabled:pointer-events-none disabled:shadow-none"
              type="submit"
              disabled={!acceptedConsent || !birthDate || loading}
            >
              {loading ? 'Cadastrando...' : 'Cadastrar'}
              <span className="material-symbols-outlined text-lg transition-transform group-hover:translate-x-1">arrow_forward</span>
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-surface-container-low text-center relative z-10">
            <p className="text-sm text-on-surface-variant">
              Já tem uma conta?{' '}
              <Link className="text-primary font-medium hover:text-primary-container transition-colors" to="/login">Entrar</Link>
            </p>
            <div className="mt-3 text-xs text-on-surface-variant/50 space-x-3">
              <a href="/termos" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">Termos de Uso</a>
              <span>•</span>
              <a href="/privacidade" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">Política de Privacidade</a>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default RegisterPage;
